import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, Image as ImageIcon, Link, Bold, Italic, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/auth';
import { createSignature, updateSignature } from '../lib/nylasService';

interface UserSignatureEditorProps {
  onClose: () => void;
  onSave: () => void;
}

export default function UserSignatureEditor({ onClose, onSave }: UserSignatureEditorProps) {
  const { user, initialize } = useAuthStore();
  const [signature, setSignature] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchUserSignature();
  }, []);

  const [nylasSignatureId, setNylasSignatureId] = useState<string | null>(null);

  const fetchUserSignature = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('user_settings')
        .select('email_signature, nylas_signature_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        if (data.email_signature) setSignature(data.email_signature);
        if (data.nylas_signature_id) setNylasSignatureId(data.nylas_signature_id);
      }
    } catch (err: any) {
      console.error('Error fetching user signature:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Save locally first
      const { error: dbError } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user?.id,
          email_signature: signature
        }, {
          onConflict: 'user_id'
        });

      if (dbError) throw dbError;

      // Push to Nylas (create or update)
      try {
        if (nylasSignatureId) {
          await updateSignature(nylasSignatureId, signature);
        } else {
          const result = await createSignature(signature);
          if (result?.signature_id) {
            setNylasSignatureId(result.signature_id);
          }
        }
      } catch (nylasErr: any) {
        console.warn('Nylas signature sync failed (will auto-migrate on next send):', nylasErr);
      }

      setSuccess(true);
      setTimeout(() => {
        initialize();
        onSave();
      }, 1500);
    } catch (err: any) {
      console.error('Error saving signature:', err);
      setError(err.message);
    }
    setLoading(false);
  };

  const insertImage = () => {
    const imageUrl = prompt('Enter image URL:');
    if (imageUrl) {
      const imgTag = `<img src="${imageUrl}" alt="Signature Image" style="max-width: 200px; max-height: 100px;" />`;
      setSignature(prev => prev + imgTag);
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    const text = prompt('Enter link text:');
    if (url && text) {
      const linkTag = `<a href="${url}" target="_blank" style="color: #3b82f6; text-decoration: none;">${text}</a>`;
      setSignature(prev => prev + linkTag);
    }
  };

  const applyFormatting = (format: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString();
    
    if (!selectedText) return;
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'alignLeft':
        formattedText = `<div style="text-align: left;">${selectedText}</div>`;
        break;
      case 'alignCenter':
        formattedText = `<div style="text-align: center;">${selectedText}</div>`;
        break;
      case 'alignRight':
        formattedText = `<div style="text-align: right;">${selectedText}</div>`;
        break;
      default:
        return;
    }
    
    // Get the textarea element
    const textarea = document.getElementById('signature-editor') as HTMLTextAreaElement;
    if (!textarea) return;
    
    // Replace the selected text with the formatted text
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = textarea.value;
    
    setSignature(
      currentValue.substring(0, start) + 
      formattedText + 
      currentValue.substring(end)
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Email Signature Editor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-md text-sm">
            Signature saved successfully!
          </div>
        )}

        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">
            Create your email signature below. You can use HTML formatting and add images or links.
          </p>
          
          <div className="flex space-x-2 mb-2">
            <button 
              onClick={() => applyFormatting('bold')}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </button>
            <button 
              onClick={() => applyFormatting('italic')}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </button>
            <button 
              onClick={() => applyFormatting('alignLeft')}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Align Left"
            >
              <AlignLeft className="h-4 w-4" />
            </button>
            <button 
              onClick={() => applyFormatting('alignCenter')}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Align Center"
            >
              <AlignCenter className="h-4 w-4" />
            </button>
            <button 
              onClick={() => applyFormatting('alignRight')}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Align Right"
            >
              <AlignRight className="h-4 w-4" />
            </button>
            <button 
              onClick={insertImage}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Insert Image"
            >
              <ImageIcon className="h-4 w-4" />
            </button>
            <button 
              onClick={insertLink}
              className="p-2 border border-gray-300 rounded hover:bg-gray-100"
              title="Insert Link"
            >
              <Link className="h-4 w-4" />
            </button>
          </div>
          
          <textarea
            id="signature-editor"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="w-full h-64 p-4 border border-gray-300 rounded-md font-mono text-sm"
            placeholder="Enter your email signature here. You can use HTML tags for formatting."
          />
        </div>

        <div className="mt-6 bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
          <div 
            className="p-4 border border-gray-200 rounded-md bg-white min-h-[100px]"
            dangerouslySetInnerHTML={{ __html: signature }}
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Signature
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}